/**
 * The two LIVE Throughline themes, transcribed VERBATIM from the U4 palette
 * comparison sheet (docs/design/sage-palettes-comparison-2026-07-05.html —
 * the authoritative source; cross-checked against the two one-sheets,
 * styleguide-onesheet-{sage-teal,ember-gray}-2026-07-05.html). Do not
 * hand-tune a colour here — if a value needs to change, change it in the
 * comparison sheet first and re-transcribe, so this file and the design
 * source never drift apart.
 *
 * LANE U6 (Jason, verbatim: "sage teal & Ember Grey — save all other to the
 * designs repos and all docs"): this REPLACES the prior three-theme set
 * (warm-paper / sage-calm / dusk) with exactly two — Sage Teal as the light
 * DEFAULT_THEME, Ember Gray as the dark mode. The three replaced themes are
 * NOT deleted — every one of their token values is preserved verbatim in
 * docs/design/palette-archive-2026-07-05.md (the no-deletion law), alongside
 * a one-line "why set aside" note for the un-chosen U4 proposals (Hearth,
 * Hearthlight Mist, Soft Blue) and the parked orange (Sunlit Olive). This is
 * an intentional 2-theme set, not a placeholder for more — ThemeSwatches
 * still renders every key in THEME_ORDER, whatever that array holds.
 *
 * Theme keys are the HONEST palette names ('sage-teal' / 'ember-gray'), not
 * the old warm-paper/sage-calm/dusk identifiers — a key that no longer
 * matches its own palette's real character is worse than a one-time rename.
 * ThemeProvider's readStoredTheme() validates against these same two keys and
 * falls back to DEFAULT_THEME for anything else (including a stale
 * warm-paper/sage-calm/dusk value from a user's earlier localStorage) — see
 * that file's own migration-safety comment.
 *
 * LANE W2.1-BUCKETS EXTENSION (Jason, direct: "I want a different color on
 * each as well"): the six `bucket*`/`bucket*Soft` pairs below are a
 * DELIBERATE, NEW addition on top of the mockup-transcribed base tokens
 * above — not a hand-tune of an existing mockup value (the "do not
 * hand-tune" law above still governs bg/surf/.../good/shadow/swatch
 * untouched). Each bucket gets its OWN hue, chosen to harmonize with the
 * theme's existing palette while being visually distinct from every other
 * bucket's hue AND from the pre-existing good/accent/warm/error/ink-soft
 * tokens (so a bucket chip is never confusable with an unrelated
 * "done"/"warning"/"muted" signal elsewhere in the UI). Each `bucketX` is
 * used as TEXT color on its OWN `bucketXSoft` tint background — the exact
 * same pairing idiom `accent`/`accentSoft` already establishes — so no new
 * ink-contrast pairing is needed (a saturated color as small text on its own
 * pale tint reads reliably across every theme, unlike using it as a solid
 * fill). Every bucket color still routes through a named token, never a raw
 * hex in a component.
 *
 * LANE U6 TOKEN-VOCABULARY MERGE — this file's prior shape spoke `support`/
 * `supportSoft` (sage-calm's value, #C2724E salmon, doubled as BOTH
 * warm-accent surface AND the app's only stand-in for an error state — the
 * confusion Jason flagged directly: "reads as an error"). That single token
 * is now split into the two U4 introduces:
 *   - `warm`/`warmInk`/`warmSoft` — an honest-fact warm-accent surface
 *     (sponsorship confirmed, a tracked/applied marker, a DEMO band) — never
 *     a failure state.
 *   - `error`/`errorInk`/`errorSoft` — the app's first REAL error token,
 *     used only for genuine failures (a card move that didn't save, a
 *     blocked-by-the-render-gate state). Every consumer of the old
 *     `--tl-support`/`--tl-support-soft` CSS vars has been repointed to
 *     whichever of these two actually matches its meaning (see themes.ts's
 *     sibling files' own repoint comments) — never left on a token this file
 *     no longer defines.
 */

export type ThemeName = 'sage-teal' | 'ember-gray';

export interface ThemeTokens {
  bg: string;
  surf: string;
  surf2: string;
  ink: string;
  inkSoft: string;
  line: string;
  accent: string;
  accentInk: string;
  accentSoft: string;
  /** An honest-fact warm-accent surface (sponsorship confirmed, a
   *  tracked/applied marker, a DEMO band) — never a failure state. Replaces
   *  the old `support`/`supportSoft` pair's warm-accent half. */
  warm: string;
  warmInk: string;
  warmSoft: string;
  /** The app's first real error token — genuine failures only (a card move
   *  that didn't save, a blocked-by-lint state). Replaces the old
   *  `support`/`supportSoft` pair's error-standing-in half. */
  error: string;
  errorInk: string;
  errorSoft: string;
  good: string;
  shadow: string;
  /** LANE W-NAV — the side-nav drawer's scrim overlay. A dedicated token
   *  (not derived from `ink`/`shadow`) because a scrim's job — darken
   *  everything behind it while a modal is open — needs the SAME dark,
   *  translucent value regardless of theme: `ink` flips to near-white in
   *  ember-gray (correct for TEXT, wrong for a dimming overlay), and
   *  `shadow` is a full box-shadow shorthand, not an extractable color. Both
   *  themes therefore define this as a black-based rgba (the conventional
   *  scrim color), same idiom as `warm`/`error` being split out as their own
   *  purpose-built tokens rather than reusing an ill-fitting existing one
   *  (see this interface's own header comment on that split). */
  scrim: string;
  /** The dot shown in the theme-swatch picker — same as `accent` in every
   *  theme today, kept distinct because the mockup names it separately. */
  swatch: string;
  // ── LANE W2.1-BUCKETS: one distinct hue + its own soft tint, per bucket ──
  /** home — "no relocation stress," the safest/most positive bucket. */
  bucketHome: string;
  bucketHomeSoft: string;
  /** relocate — a domestic move, no visa involved. */
  bucketRelocate: string;
  bucketRelocateSoft: string;
  /** bridge — sponsorship in progress, a foreign country. */
  bucketBridge: string;
  bucketBridgeSoft: string;
  /** dream — an aspirational company/title match. */
  bucketDream: string;
  bucketDreamSoft: string;
  /** pass — the user's own explicit "not a current fit." */
  bucketPass: string;
  bucketPassSoft: string;
  /** unsorted — needs the user's attention to sort. */
  bucketUnsorted: string;
  bucketUnsortedSoft: string;
  /** BUG-2026-07-08 PACKAGE E — undetermined (bucket=NULL, never scored at
   *  all) is its OWN distinct hue, no longer a reuse of bucketUnsorted's rose
   *  (his words: "two unsorted tabs... Unacceptable" — same complaint
   *  applies to sharing a color as sharing a label). A cooler, more neutral
   *  slate tone reads as "not yet looked at" vs unsorted's warmer "looked at,
   *  still undecided" rose — a deliberate, distinguishable pair, never an
   *  arbitrary seventh hue. */
  bucketUndetermined: string;
  bucketUndeterminedSoft: string;
}

export const THEMES: Record<ThemeName, ThemeTokens> = {
  'sage-teal': {
    bg: '#E2EBE7',
    surf: '#FFFFFF',
    surf2: '#D2E0DB',
    ink: '#1E2A28',
    inkSoft: '#526460',
    line: 'rgba(30,42,40,0.10)',
    accent: '#3D7A72',
    accentInk: '#FFFFFF',
    accentSoft: '#DCEAE7',
    warm: '#E0B26A',
    warmInk: '#33260F',
    warmSoft: '#F5E9D3',
    error: '#A6423F',
    errorInk: '#FFFFFF',
    errorSoft: '#F1DEDC',
    good: '#356963',
    shadow: '0 16px 40px rgba(30,55,50,0.09)',
    scrim: 'rgba(20,28,26,0.45)',
    swatch: '#3D7A72',
    bucketHome: '#2F6B2F',
    bucketHomeSoft: '#DAE9D9',
    bucketRelocate: '#1861A0',
    bucketRelocateSoft: '#D4E5F0',
    bucketBridge: '#83590F',
    bucketBridgeSoft: '#F0E1BE',
    bucketDream: '#5C4A8E',
    bucketDreamSoft: '#E3DBEF',
    bucketPass: '#52604F',
    bucketPassSoft: '#DEE6D9',
    bucketUnsorted: '#8A4256',
    bucketUnsortedSoft: '#F0DBE1',
    bucketUndetermined: '#4A5568',
    bucketUndeterminedSoft: '#E1E5EA',
  },
  'ember-gray': {
    bg: '#232323',
    surf: '#2C2C2C',
    surf2: '#363636',
    ink: '#EDEBE6',
    inkSoft: '#A6A39C',
    line: 'rgba(237,235,230,0.10)',
    accent: '#9DBB8E',
    accentInk: '#1C2019',
    accentSoft: '#333A2F',
    warm: '#D9A85E',
    warmInk: '#2A1D0C',
    warmSoft: '#3A311F',
    error: '#E08C86',
    errorInk: '#241414',
    errorSoft: '#3A2624',
    good: '#9DBB8E',
    shadow: '0 18px 46px rgba(0,0,0,0.45)',
    scrim: 'rgba(0,0,0,0.55)',
    swatch: '#9DBB8E',
    bucketHome: '#8FC98A',
    bucketHomeSoft: '#2A3226',
    bucketRelocate: '#7FCBD1',
    bucketRelocateSoft: '#22322F',
    bucketBridge: '#E0B25E',
    bucketBridgeSoft: '#39301E',
    bucketDream: '#BBA3DE',
    bucketDreamSoft: '#302B3A',
    bucketPass: '#B7A996',
    bucketPassSoft: '#302E27',
    bucketUnsorted: '#E093A6',
    bucketUnsortedSoft: '#382528',
    bucketUndetermined: '#9AA5B1',
    bucketUndeterminedSoft: '#2C3138',
  },
};

export const DEFAULT_THEME: ThemeName = 'sage-teal';

export const THEME_ORDER: ThemeName[] = ['sage-teal', 'ember-gray'];

/** Themes with a dark surface — flips things like status-bar / meta theme-color
 *  and any `isDark`-branched rendering (mirrors the mockup's `isDark` flag). */
export const DARK_THEMES = new Set<ThemeName>(['ember-gray']);

export function isDarkTheme(name: ThemeName): boolean {
  return DARK_THEMES.has(name);
}

/** Serialise a theme's tokens into the `--var` map applied to the shell element,
 *  matching the mockup's inline `shell` style object 1:1 (including the two
 *  fixed typography vars, which do not vary per theme). */
export function themeCssVars(name: ThemeName): Record<string, string> {
  const t = THEMES[name];
  return {
    '--tl-bg': t.bg,
    '--tl-surf': t.surf,
    '--tl-surf2': t.surf2,
    '--tl-ink': t.ink,
    '--tl-ink-soft': t.inkSoft,
    '--tl-line': t.line,
    '--tl-accent': t.accent,
    '--tl-accent-ink': t.accentInk,
    '--tl-accent-soft': t.accentSoft,
    '--tl-warm': t.warm,
    '--tl-warm-ink': t.warmInk,
    '--tl-warm-soft': t.warmSoft,
    '--tl-error': t.error,
    '--tl-error-ink': t.errorInk,
    '--tl-error-soft': t.errorSoft,
    '--tl-good': t.good,
    '--tl-shadow': t.shadow,
    // LANE W-NAV — the side-nav drawer's scrim (see ThemeTokens' own doc
    // comment on `scrim` for why this is its own token, not derived from
    // `ink`/`shadow`).
    '--tl-scrim': t.scrim,
    '--tl-serif': "'Newsreader', Georgia, serif",
    '--tl-sans': "'Hanken Grotesk', system-ui, sans-serif",
    // LANE W2.1-BUCKETS — six distinct per-bucket text/soft-tint pairs (see
    // ThemeTokens' own doc comment: additive, never a hand-tune of the
    // mockup base).
    '--tl-bucket-home': t.bucketHome,
    '--tl-bucket-home-soft': t.bucketHomeSoft,
    '--tl-bucket-relocate': t.bucketRelocate,
    '--tl-bucket-relocate-soft': t.bucketRelocateSoft,
    '--tl-bucket-bridge': t.bucketBridge,
    '--tl-bucket-bridge-soft': t.bucketBridgeSoft,
    '--tl-bucket-dream': t.bucketDream,
    '--tl-bucket-dream-soft': t.bucketDreamSoft,
    '--tl-bucket-pass': t.bucketPass,
    '--tl-bucket-pass-soft': t.bucketPassSoft,
    '--tl-bucket-unsorted': t.bucketUnsorted,
    '--tl-bucket-unsorted-soft': t.bucketUnsortedSoft,
    // BUG-2026-07-08 PACKAGE E — undetermined's own token pair, distinct from
    // unsorted (see ThemeTokens.bucketUndetermined's own doc comment).
    '--tl-bucket-undetermined': t.bucketUndetermined,
    '--tl-bucket-undetermined-soft': t.bucketUndeterminedSoft,
  };
}
